<template>
  <div class="student-list-container">
    <div class="page-header">
      <div class="breadcrumb">ນັກຮຽນ / <span>ລາຍຊື່ຂອງນັກຮຽນ</span></div>
      <div class="header-content">
        <h1>ລາຍຊື່ຂອງນັກຮຽນ</h1>
        
        <div style="display:flex;gap:10px;align-items:center;">
          <button v-if="hasBit($route.query.id, 1)" class="btn-primary" @click="$router.push('/home/students/new')">
            <i class="fas fa-user-plus"></i> ເພີ່ມນັກຮຽນໃໝ່
          </button>
          <button class="btn-export-emis" @click="exportEmis" :disabled="exportingEmis">
            <i class="fas fa-file-excel"></i> {{ exportingEmis ? 'ກຳລັງ Export...' : 'Export EMIS Excel' }}
          </button>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-head">
        <div class="search-box">
          <div class="input-container">
            <i class="fas fa-search search-icon"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              @keyup.enter="handleSearch" 
              placeholder="ຄົ້ນຫາດ້ວຍ ຊື່, ນາມສະກຸນ ຫຼື ລະຫັດ..."
            >
          </div>
          <button class="btn-search" @click="handleSearch">ຄົ້ນຫາ</button>
        </div>
      </div>

      <div class="panel-body">
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>ລຳດັບ</th>
                <th>ລະຫັດນັກຮຽນ</th>
                <th>ຊື່ ແລະ ນາມສະກຸນ</th>
                <th>ເພດ</th>
                <th>ວັນເດືອນປີເກີດ</th>
                <th>ຊັ້ນຮຽນ</th>
                <th>ສົກຮຽນ</th>
                <th class="text-center">ຈັດການ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(student, index) in students" :key="student._id">
                <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                <td><span class="asset-code">{{ student.student_id }}</span></td>
                <td class="asset-name">{{ student.firstNameLao }} {{ student.familyNameLao }}</td>
                <td>{{ student.gender }}</td>
                <td>{{ formatDate(student.dob) }}</td>
                <td>{{ student.class_id }}</td>
                <td>{{ student.academic_year }}</td>
                <td class="text-center">
                  <button v-if="hasBit('student-list', 1)" class="panel-action" @click="handleEdit(student)" title="ແກ້ໄຂ">✏️</button>
                  <button v-if="hasBit('student-list', 2)" class="panel-action text-danger" @click="confirmDelete(student)" title="ລົບ">🗑️</button>
                </td>
              </tr>
              <tr v-if="students.length === 0">
                <td colspan="8" class="text-center py-4">ບໍ່ມີຂໍ້ມູນນັກຮຽນ</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- ສ່ວນຄວບຄຸມ Pagination -->
      <div class="pagination-container">
        <div class="pagination-info">ສະແດງ {{ startItem }} - {{ endItem }} ຈາກທັງໝົດ {{ totalRows }} ລາຍການ</div>
        <div class="pagination-controls">
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">«</button>
          <template v-for="(page, idx) in visiblePages">
            <span v-if="page === '...'" :key="'e' + idx" class="page-ellipsis">…</span>
            <button v-else :key="page"
                    class="page-btn"
                    :class="{ active: currentPage === page }"
                    @click="changePage(page)">{{ page }}</button>
          </template>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">»</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api';
import { exportEmisStudents } from '@/utils/emisExport';
import { getPaginationRange } from '@/utils/pagination';

export default {
  name: 'StudentListPage',
  data() {
    return {
      students: [],
      currentPage: 1,
      pageSize: 20,
      totalRows: 0,
      searchQuery: '',
      loading: false,
      exportingEmis: false,
      user: {},
      permissionState: {},
      permissionRecordId: null,
      loadingPermissions: false,
      searchTimer: null
    }
  },
  watch: {
    // ຕິດຕາມການປ່ຽນແປງຂອງ searchQuery ແລະ ຄົ້ນຫາໂດຍອັດຕະໂນມັດ
    searchQuery() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      // ໜ່ວງເວລາ 500ms ກ່ອນຈະຄົ້ນຫາ (Debounce) ເພື່ອບໍ່ໃຫ້ API ເຮັດວຽກໜັກເກີນໄປໃນຂະນະທີ່ພິມ
      this.searchTimer = setTimeout(() => {
        this.handleSearch();
      }, 500);
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalRows / this.pageSize) || 1;
    },
    visiblePages() {
      return getPaginationRange(this.currentPage, this.totalPages);
    },
    startItem() {
      return this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    },
    endItem() {
      const end = this.currentPage * this.pageSize;
      return end > this.totalRows ? this.totalRows : end;
    }
  },
  mounted() {
    this.loadUserInfo();
    this.fetchStudents();
    this.fetchRolePermissions(this.user.user_level);
  },
  methods: {
    loadUserInfo() {
      const savedInfo = localStorage.getItem('user-info');
      if (savedInfo) {
        try {
          this.user = JSON.parse(savedInfo);
        } catch (e) {
          console.error('Error parsing user-info', e);
          this.user = { username: 'Guest' };
        }
      }
    },
    async fetchStudents() {
      this.loading = true;
      try {
        const response = await api.get('/students', {
          params: {
            page: this.currentPage,
            limit: this.pageSize,
            search: this.searchQuery,
            school_id: this.user.school_id // ສົມມຸດໃຫ້ເພີ່ມ school_id ເຂົ້າໃນ params
          }
        });

        if (response.data.code === 1) {
          this.students = response.data.rows;
          this.totalRows = response.data.totalRows || response.data.rows.length; // ໃຫ້ Backend ສົ່ງ totalRows ມາ
        }
      } catch (error) {
        console.error('Error fetching students:', error);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.currentPage = 1; // ເລີ່ມຕົ້ນໜ້າ 1 ໃໝ່ເມື່ອມີການຄົ້ນຫາ
      this.fetchStudents();
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('lo-LA', { year: 'numeric', month: 'numeric', day: 'numeric' });
    },
    async confirmDelete(student) {
      if (confirm(`ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລົບນັກຮຽນ: ${student.firstNameLao} ${student.familyNameLao}?`)) {
        try {
          const response = await api.delete(`/del/students/${student._id}`); // ສົມມຸດວ່າ Endpoint ລົບແມ່ນ /students/:id
          if (response.data.code === 1) {
            alert('ລົບຂໍ້ມູນສຳເລັດ');
            this.fetchStudents(); // ໂຫຼດຂໍ້ມູນໃໝ່ຫຼັງຈາກລົບ
          }
        } catch (error) {
          alert('ເກີດຂໍ້ຜິດພາດໃນການລົບ');
        }
      }
    },
    handleEdit(student) {
      // ສົມມຸດວ່າໜ້າແກ້ໄຂນັກຮຽນແມ່ນ /home/students/edit/:id
      this.$router.push(`/home/students/edit/${student._id}`);
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchStudents();
      }
    },
    async fetchRolePermissions(roleId) {
      this.loadingPermissions = true;
      try {
        const res = await api.get(`/getRowsInt/permissions/role_id/${roleId}`);
        if (res.data && res.data.length > 0) {
          this.permissionState = res.data[0].permissions || {};
          this.permissionRecordId = res.data[0]._id;
        } else {
          // ຫາກບໍ່ພົບຂໍ້ມູນ ໃຫ້ຕັ້ງຄ່າເລີ່ມຕົ້ນ (ສິດເບິ່ງຢ່າງດຽວ ສໍາລັບ User ທົ່ວໄປ)
          this.permissionRecordId = null;
          const defaults = {};
          if (this.menuGroups) {
            this.menuGroups.forEach(group => {
              defaults[group.id] = roleId === 1 ? 7 : 1;
              group.items.forEach(item => {
                defaults[item.id] = roleId === 1 ? 7 : 1;
              });
            });
          }
          this.permissionState = defaults;
        }
      } catch (e) {
        console.error('Fetch permissions error:', e);
      } finally {
        this.loadingPermissions = false;
      }
    },
    hasBit(moduleKey, bit) {
      const val = this.permissionState[moduleKey] || 0;
      return (val & (1 << bit)) !== 0;
    },
    async exportEmis() {
      this.exportingEmis = true;
      try {
        // ດຶງຂໍ້ມູນທັງໝົດ (ບໍ່ມີ pagination) ສຳລັບ export
        const res = await api.get('/students', {
          params: {
            page: 1,
            limit: 9999,
            school_id: this.user.school_id,
          }
        });
        if (res.data.code === 1 && res.data.rows.length > 0) {
          const filename = `EMIS_StudentProfile_${this.user.school_id || 'school'}`;
          exportEmisStudents(res.data.rows, filename);
        } else {
          alert('ບໍ່ມີຂໍ້ມູນນັກຮຽນທີ່ຈະ Export');
        }
      } catch (e) {
        console.error('Export EMIS error:', e);
        alert('ເກີດຂໍ້ຜິດພາດໃນການ Export');
      } finally {
        this.exportingEmis = false;
      }
    },
  }
}
</script>

<style scoped>
.student-list-container { animation: fadeIn 0.4s ease; }
.header-content { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
.search-box { display: flex; gap: 10px; padding: 15px 20px; background: #fcfcfc; border-radius: 10px 10px 0 0; }
.input-container { position: relative; flex: 1; max-width: 400px; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text3); }
.input-container input { width: 100%; padding: 10px 10px 10px 35px; border: 1px solid var(--border); border-radius: 8px; font-size: 14px; }
.btn-search { background: var(--lao); color: white; border: none; padding: 0 20px; border-radius: 8px; cursor: pointer; font-weight: 500; }
.pagination-container { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-top: 1px solid var(--border); }
.page-btn { padding: 5px 12px; border: 1px solid var(--border); background: white; border-radius: 4px; cursor: pointer; margin: 0 2px; }
.page-btn.active { background: var(--lao); color: white; border-color: var(--lao); }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-ellipsis { padding: 5px 6px; color: var(--text3); }
.text-danger { color: var(--danger) !important; }
.text-center { text-align: center; }
.py-4 { padding-top: 1.5rem; padding-bottom: 1.5rem; }
.btn-primary {
  background: var(--lao);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}
.btn-primary:hover { background: var(--lao-mid); }
.btn-export-emis {
  background: #16a34a;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  transition: 0.2s;
}
.btn-export-emis:hover { background: #15803d; }
.btn-export-emis:disabled { opacity: 0.6; cursor: not-allowed; }
</style>